import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, TrendingUp, Share2, Settings, Eye, RefreshCw, BarChart2, Copy, Download, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { renderShareCard, downloadBlob, shareBlob } from '../lib/shareCard';
import { usePWAInstall } from '../hooks/usePWAInstall';

const CHART_PERIODS = [
  { id: '1D', label: '1D', months: 1/22, numPoints: 24, stepVol: 0.012, labelText: 'Today',         apiPeriod: '7d'  },
  { id: '1W', label: '1W', months: 1/4.33, numPoints: 14, stepVol: 0.025, labelText: 'Past week',   apiPeriod: '7d'  },
  { id: '1M', label: '1M', months: 1, numPoints: 16, stepVol: 0.038, labelText: 'Past month',       apiPeriod: '30d' },
  { id: '3M', label: '3M', months: 3, numPoints: 20, stepVol: 0.055, labelText: 'Past 3 months',    apiPeriod: '90d' },
  { id: '6M', label: '6M', months: 6, numPoints: 22, stepVol: 0.075, labelText: 'Past 6 months',    apiPeriod: '90d' },
  { id: '1Y', label: '1Y', months: 12, numPoints: 24, stepVol: 0.095, labelText: 'Past year',       apiPeriod: 'ytd' },
  { id: 'ALL', label: 'ALL', months: 24, numPoints: 28, stepVol: 0.120, labelText: 'All time',      apiPeriod: 'all' },
];

const PROJECTION_PERIODS = [
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '180D', days: 180 },
  { label: '1Y', days: 365 },
];

const COPY_MULTIPLIERS = [
  { label: '0.5x', value: 0.5, desc: 'Conservative', riskNote: 'Risk reduced by 50%' },
  { label: '0.75x', value: 0.75, desc: 'Moderate', riskNote: 'Risk reduced by 25%' },
  { label: '1x', value: 1.0, desc: 'Standard', riskNote: 'Standard risk level' },
  { label: '1.5x', value: 1.5, desc: 'Aggressive', riskNote: 'Risk increases by 50%' },
  { label: '2x', value: 2.0, desc: 'High Risk', riskNote: 'Risk increases by 100%' },
];

function generateChartData(startBalance, endBalance, periodCfg) {
  const { numPoints, stepVol, months } = periodCfg;
  let lcgState = (0xdeadbeef ^ (Math.round(startBalance) & 0xfffffff)) + months * 31337;
  const lcgRand = () => { lcgState = Math.imul(lcgState, 1664525) + 1013904223; return ((lcgState >>> 0) / 0xffffffff); };
  const rawNoise = Array.from({ length: numPoints }, () => lcgRand() - 0.5);
  const cs = rawNoise.reduce((acc, v, i) => { acc.push((acc[i - 1] || 0) + v); return acc; }, []);
  const bridge = cs.map((c, i) => c - (i / (numPoints - 1)) * cs[numPoints - 1]);
  const logReturn = Math.log(endBalance / startBalance);
  const data = [];
  for (let i = 0; i < numPoints; i++) {
    let val;
    if (i === 0) val = startBalance;
    else if (i === numPoints - 1) val = endBalance;
    else { val = Math.round(startBalance * Math.exp((i / (numPoints - 1)) * logReturn + stepVol * bridge[i])); val = Math.max(val, Math.round(startBalance * 0.78)); }
    data.push({ value: parseFloat(((val - startBalance) / startBalance * 100).toFixed(2)), raw: val });
  }
  return data;
}

// Custom SVG chart with Y-axis % labels - React 19 compatible
function EquityChart({ data, color, height = 160 }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range  = maxVal - minVal || 1;
  const padTop = 8, padBot = 8, padLeft = 44, padRight = 4;
  const chartW = 560;
  const chartH = height - padTop - padBot;

  const getX = (i) => padLeft + (i / (data.length - 1)) * (chartW - padLeft - padRight);
  const getY = (v) => padTop + chartH - ((v - minVal) / range) * chartH;

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i).toFixed(1)},${getY(d.value).toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L ${getX(data.length-1).toFixed(1)},${(padTop+chartH).toFixed(1)} L ${getX(0).toFixed(1)},${(padTop+chartH).toFixed(1)} Z`;

  // Y-axis labels — 4 evenly spaced percentage marks
  const yLabels = [0, 0.25, 0.5, 0.75, 1.0].map(pct => {
    const v = minVal + pct * range;
    const y = getY(v).toFixed(1);
    const label = v >= 1000 ? `+${(v/1000).toFixed(1)}k%` : v >= 0 ? `+${v.toFixed(0)}%` : `${v.toFixed(0)}%`;
    return { y, label };
  });

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${chartW} ${height}`} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={`grad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.45} />
          <stop offset="100%" stopColor={color} stopOpacity={0.03} />
        </linearGradient>
      </defs>
      {/* Y-axis grid + labels */}
      {yLabels.map((yl, i) => (
        <g key={i}>
          <line x1={padLeft} y1={yl.y} x2={chartW - padRight} y2={yl.y} stroke="rgba(39,39,42,0.4)" strokeDasharray="3 3" />
          <text x={padLeft - 4} y={parseFloat(yl.y) + 4} textAnchor="end" fontSize="9" fill="rgba(161,161,170,0.6)" fontFamily="monospace">{yl.label}</text>
        </g>
      ))}
      {/* Area fill */}
      <path d={areaPath} fill={`url(#grad-${color.replace('#','')})`} />
      {/* Line */}
      <path d={linePath} stroke={color} strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Multi-series chart — renders one line per account for the "Per Account" view.
// Each series is {label, color, points: [{value, raw, t}]}. X axis uses the
// timestamps so all series align on the same real time scale.
function MultiSeriesEquityChart({ series, height = 210 }) {
  if (!series || series.length === 0) return null;
  const valid = series.filter(s => s.points && s.points.length >= 2);
  if (valid.length === 0) return null;

  // Build global X (time) and Y (value) ranges across every series
  const allTimes = valid.flatMap(s => s.points.map(p => new Date(p.t).getTime()));
  const allVals  = valid.flatMap(s => s.points.map(p => p.value));
  const tMin = Math.min(...allTimes), tMax = Math.max(...allTimes);
  const vMin = Math.min(...allVals),  vMax = Math.max(...allVals);
  const tRange = (tMax - tMin) || 1;
  const vRange = (vMax - vMin) || 1;

  const padTop = 8, padBot = 8, padLeft = 44, padRight = 4;
  const chartW = 560, chartH = height - padTop - padBot;
  const getX = (t) => padLeft + ((new Date(t).getTime() - tMin) / tRange) * (chartW - padLeft - padRight);
  const getY = (v) => padTop + chartH - ((v - vMin) / vRange) * chartH;

  const yLabels = [0, 0.25, 0.5, 0.75, 1.0].map(pct => {
    const v = vMin + pct * vRange;
    const y = getY(v).toFixed(1);
    const label = v >= 1000 ? `+${(v/1000).toFixed(1)}k%` : v >= 0 ? `+${v.toFixed(0)}%` : `${v.toFixed(0)}%`;
    return { y, label };
  });

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${chartW} ${height}`} style={{ display: 'block', overflow: 'visible' }}>
      {yLabels.map((yl, i) => (
        <g key={i}>
          <line x1={padLeft} y1={yl.y} x2={chartW - padRight} y2={yl.y} stroke="rgba(39,39,42,0.4)" strokeDasharray="3 3" />
          <text x={padLeft - 4} y={parseFloat(yl.y) + 4} textAnchor="end" fontSize="9" fill="rgba(161,161,170,0.6)" fontFamily="monospace">{yl.label}</text>
        </g>
      ))}
      {valid.map((s, idx) => {
        const d = s.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(p.t).toFixed(1)},${getY(p.value).toFixed(1)}`).join(' ');
        return (
          <path key={s.label + idx} d={d} stroke={s.color} strokeWidth={2} fill="none"
                strokeLinecap="round" strokeLinejoin="round" opacity={0.95} />
        );
      })}
    </svg>
  );
}

// Convert raw backend points [{t, equity, cum_pnl}] to the {value, raw, t} shape
// expected by the single-series chart (value = % return vs starting balance).
function pointsToChartData(points, startingBalance) {
  if (!points || points.length === 0) return [];
  const sb = startingBalance || 1;
  return points.map(p => ({
    value: parseFloat((((p.equity - sb) / sb) * 100).toFixed(2)),
    raw:   p.equity,
    t:     p.t,
  }));
}


// Mini projection chart for the Growth Projector
function ProjectionChart({ startBalance, monthlyRate, selectedPeriod, height = 110 }) {
  const months = Math.round(selectedPeriod / 30);
  const points = Array.from({ length: months + 1 }, (_, i) => ({
    value: Math.round(startBalance * Math.pow(1 + monthlyRate / 100, i)),
    label: i === 0 ? 'Now' : `${i}m`,
  }));
  if (points.length < 2) return null;
  const values = points.map(p => p.value);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const range = maxV - minV || 1;
  const padTop = 8, padBot = 18, padLeft = 8, padRight = 8;
  const w = 400, chartH = height - padTop - padBot;
  const getX = (i) => padLeft + (i / (points.length - 1)) * (w - padLeft - padRight);
  const getY = (v) => padTop + chartH - ((v - minV) / range) * chartH;
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i).toFixed(1)},${getY(p.value).toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L ${getX(points.length-1).toFixed(1)},${(padTop+chartH).toFixed(1)} L ${getX(0).toFixed(1)},${(padTop+chartH).toFixed(1)} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ display: 'block' }}>
      <defs>
        <linearGradient id="projGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2979FF" stopOpacity={0.35} />
          <stop offset="100%" stopColor="#2979FF" stopOpacity={0.03} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#projGrad)" />
      <path d={linePath} stroke="#2979FF" strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        i === points.length - 1 ? (
          <circle key={i} cx={getX(i)} cy={getY(p.value)} r={4} fill="#2979FF" />
        ) : null
      ))}
      {/* X-axis labels */}
      {points.filter((_, i) => i === 0 || i === points.length - 1 || (months > 2 && i === Math.floor(months/2))).map((p, _, arr) => {
        const idx = points.indexOf(p);
        return (
          <text key={idx} x={getX(idx)} y={height - 4} textAnchor={idx === 0 ? 'start' : idx === points.length - 1 ? 'end' : 'middle'} fontSize="9" fill="rgba(161,161,170,0.6)" fontFamily="monospace">
            {p.label}
          </text>
        );
      })}
    </svg>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canInstall, showIOSBanner, triggerInstall, dismiss: dismissPWA } = usePWAInstall();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => sessionStorage.getItem('pp_onboarding_dismissed') === '1');
  const [edgeScore, setEdgeScore] = useState(null);
  const [fearGreed, setFearGreed] = useState(null);
  const [vix, setVix] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [chartPeriod, setChartPeriod] = useState('1Y');
  const [chartDetailMode, setChartDetailMode] = useState('simple');
  const [chartViewMode, setChartViewMode] = useState('all'); // 'all' (combined) | 'per_account'
  const [equityCurve, setEquityCurve]     = useState(null);  // {starting_balance, current_equity, points, accounts?}
  const [equityLoading, setEquityLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(90);
  const [selectedMultiplier, setSelectedMultiplier] = useState(1.0);
  const [projectionStrategyId, setProjectionStrategyId] = useState('bundle');
  const [showEdgeInfo, setShowEdgeInfo] = useState(false);
  // Account type + platform filters
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');   // 'all' | 'retail' | 'prop_firm'
  const [selectedPlatform, setSelectedPlatform] = useState(null);       // null | 'tradelocker' | 'metatrader' | …
  const [deselectedIds, setDeselectedIds] = useState(new Set());
  const [milestoneModal, setMilestoneModal] = useState(null);
  const [showInsuranceInfo, setShowInsuranceInfo] = useState(false);
  // Share Performance Card
  const [shareModal, setShareModal]         = useState(false);
  const [shareCardUrl, setShareCardUrl]     = useState(null);   // object URL for preview
  const [shareCardBlob, setShareCardBlob]   = useState(null);
  const [shareCardLoading, setShareCardLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [edge, strats, fg, vixData, mode, dashboard, milestones, recs] = await Promise.all([
        api.getEdgeScore().catch(() => null),
        api.getStrategies().catch(() => []),
        api.getFearGreed().catch(() => null),
        api.getVIX().catch(() => null),
        api.getUserMode().catch(() => ({ mode: 'simple' })),
        api.getDashboardOverview().catch(() => null),
        api.checkMilestones().catch(() => ({ new_milestones: [] })),
        api.getStrategyRecommendations().catch(() => ({ recommendations: [] })),
      ]);
      setEdgeScore(edge);
      setStrategies(strats);
      setFearGreed(fg);
      setVix(vixData);
      setAdvancedMode(mode.mode === 'advanced');
      setDashboardData(dashboard);
      setRecommendations(recs?.recommendations || []);
      if (milestones?.new_milestones?.length > 0) setMilestoneModal(milestones.new_milestones[0]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Real equity curve (combined + per-account) ─────────────────────────────
  // Refetches whenever the user changes period or switches to per-account view.
  // Silent failure → synthetic chart fallback still renders.
  useEffect(() => {
    let cancelled = false;
    const periodCfg = CHART_PERIODS.find(p => p.id === chartPeriod) || CHART_PERIODS[5];
    const wantPerAcct = chartViewMode === 'per_account';
    setEquityLoading(true);
    api.getCopyEquityCurve(periodCfg.apiPeriod, wantPerAcct)
      .then(res => { if (!cancelled) setEquityCurve(res || null); })
      .catch(() => { if (!cancelled) setEquityCurve(null); })
      .finally(() => { if (!cancelled) setEquityLoading(false); });
    return () => { cancelled = true; };
  }, [chartPeriod, chartViewMode]);

  const accounts = dashboardData?.accounts || [];

  // ── Platform groups + filtered balance ─────────────────────────────────────
  const PLATFORM_META = {
    tradelocker: { label: 'TradeLocker', icon: '🔒' },
    metatrader:  { label: 'MetaTrader',  icon: '📊' },
    tradovate:   { label: 'Tradovate',   icon: '🚀' },
    matchtrader: { label: 'MatchTrader', icon: '⚡' },
  };

  const platformGroups = useMemo(() => {
    const groups = {};
    accounts.forEach(a => {
      const key = a.platform_key || a.broker_type || 'other';
      const meta = PLATFORM_META[key] || { label: a.platform || key, icon: '💼' };
      if (!groups[key]) groups[key] = { key, label: meta.label, icon: meta.icon, accounts: [] };
      groups[key].accounts.push(a);
    });
    return groups;
  }, [accounts]); // eslint-disable-line

  const filteredAccounts = useMemo(() => {
    let result = accounts;
    if (selectedPlatform)       result = result.filter(a => (a.platform_key || a.broker_type) === selectedPlatform);
    if (accountTypeFilter !== 'all') result = result.filter(a => a.account_type === accountTypeFilter);
    result = result.filter(a => !deselectedIds.has(a.account_id));
    return result;
  }, [accounts, selectedPlatform, accountTypeFilter, deselectedIds]);

  const toggleDeselect = (id) => setDeselectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });



  const topStrategy = strategies.find(s => s.strategy_id === 'strat_sass001') || strategies[0];
  const activeCopies = dashboardData?.active_copies || [];
  const totalBalance = dashboardData?.total_balance ?? 0;
  const actualPnl = dashboardData?.total_pnl || 0;
  const hasInsurance = dashboardData?.has_active_insurance || false;
  const totalInsured = dashboardData?.total_insured_amount || 0;
  const isPreviewMode = dashboardData ? (dashboardData.preview_mode ?? false) : null;
  const hasRealAccounts = dashboardData?.has_real_accounts ?? false;

  // Display balance = real balance when accounts connected, otherwise sum of filtered
  const displayBalance = hasRealAccounts
    ? (filteredAccounts.length > 0
        ? filteredAccounts.reduce((s, a) => s + (a.balance || 0), 0)
        : totalBalance > 0 ? totalBalance : 0)
    : totalBalance > 0 ? totalBalance : 0;

  const startingBalance = isPreviewMode ? 400000 : (displayBalance > 0 ? displayBalance : 400000);
  const periodCfg = CHART_PERIODS.find(p => p.id === chartPeriod) || CHART_PERIODS[5];
  // Cap at 3%/month for dashboard chart preview (≈36% annual) — prevents inflated projections
  const projMonthly = topStrategy ? Math.min(3.0, (Math.pow(1 + Math.min(topStrategy.total_return_pct, 200) / 100, 1/36) - 1) * 100) : 2.5;
  const endVal = (hasRealAccounts && !isPreviewMode)
    ? displayBalance + actualPnl
    : (actualPnl !== 0 ? startingBalance + actualPnl : Math.round(startingBalance * Math.pow(1 + projMonthly / 100, periodCfg.months)));
  // isProjected = true only when in preview mode without real accounts
  const isProjected = dashboardData?.preview_mode === true && !hasRealAccounts;

  const chartData = useMemo(() => generateChartData(startingBalance, endVal, periodCfg), [startingBalance, endVal, chartPeriod, periodCfg]);

  // Detail mode: multi-layer realistic noise simulating real market daily fluctuations
  const detailChartData = useMemo(() => {
    const raw = chartData;
    if (raw.length < 2) return raw;

    // Multi-frequency noise: trend persistence + short-term volatility + occasional spike
    let seed = 0x7f3a9c1d;
    const lcg = () => { seed = Math.imul(seed, 1664525) + 1013904223; return ((seed >>> 0) / 0xffffffff) - 0.5; };

    const POINTS_PER_SEG = 4; // 4 sub-points per source point = more granular
    const result = [];
    let momentum = 0; // persistent trending component

    for (let i = 0; i < raw.length - 1; i++) {
      const from = raw[i].value;
      const to   = raw[i + 1].value;

      for (let j = 0; j < POINTS_PER_SEG; j++) {
        const t    = j / POINTS_PER_SEG;
        const base = from + (to - from) * t;

        // Layer 1: momentum (persists 3-5 bars)
        momentum = momentum * 0.7 + lcg() * 0.3;

        // Layer 2: daily high-frequency noise (±2-5%)
        const dailyNoise = lcg() * Math.abs(base) * 0.04;

        // Layer 3: occasional bigger swing (±6-10% — happens ~15% of the time)
        const bigSwing = Math.abs(lcg()) < 0.15 ? lcg() * Math.abs(base) * 0.09 : 0;

        const val = base + momentum * Math.abs(base) * 0.025 + dailyNoise + bigSwing;
        result.push({ value: parseFloat(val.toFixed(2)), raw: 0 });
      }
    }
    result.push(raw[raw.length - 1]);
    return result;
  }, [chartData]);

  const activeChartData = chartDetailMode === 'detail' ? detailChartData : chartData;

  // Real broker-side equity curve (from /api/copy/equity-curve). Falls back to
  // synthetic preview when the user has no real accounts, or the fetch fails.
  const hasRealEquity = !!(equityCurve && equityCurve.points && equityCurve.points.length >= 2 && (equityCurve.trade_count > 0 || hasRealAccounts));
  const realCombinedChartData = useMemo(() => {
    if (!equityCurve) return [];
    return pointsToChartData(equityCurve.points, equityCurve.starting_balance || startingBalance);
  }, [equityCurve, startingBalance]);

  // Per-account series — one colored line per linked account
  const ACCOUNT_COLORS = ['#00FF94', '#2979FF', '#FFB000', '#FF3B7A', '#7C3AED', '#00D4FF', '#FF8A00'];
  const perAccountSeries = useMemo(() => {
    const accts = equityCurve?.accounts || [];
    return accts.map((a, i) => ({
      label:  a.label,
      color:  ACCOUNT_COLORS[i % ACCOUNT_COLORS.length],
      delta:  a.delta,
      starting_balance: a.starting_balance,
      current_equity:   a.current_equity,
      points: pointsToChartData(a.points, a.starting_balance),
    }));
  }, [equityCurve]);  // eslint-disable-line

  const showRealChart      = hasRealEquity && chartViewMode === 'all';
  const showPerAccountChart = hasRealEquity && chartViewMode === 'per_account' && perAccountSeries.length > 0;
  const displayChartData   = showRealChart ? realCombinedChartData : activeChartData;

  // When real equity curve is available, use it for the hero "Total Portfolio"
  // and return numbers so they match the chart perfectly.
  const realCurrentEquity = equityCurve?.current_equity ?? null;
  const realStartingBalance = equityCurve?.starting_balance ?? null;
  const effectiveEndVal          = (hasRealEquity && realCurrentEquity != null)   ? realCurrentEquity   : endVal;
  const effectiveStartingBalance = (hasRealEquity && realStartingBalance != null && realStartingBalance > 0) ? realStartingBalance : startingBalance;
  const periodReturn = ((effectiveEndVal - effectiveStartingBalance) / (effectiveStartingBalance || 1)) * 100;
  const periodReturnDisplay = (Math.abs(periodReturn) >= 1000)
      ? Math.round(periodReturn).toLocaleString()
      : periodReturn.toFixed(2);   // 2dp for normal % returns
  const periodPnl    = Math.round(effectiveEndVal - effectiveStartingBalance);
  const isPos = periodReturn >= 0;
  const chartColor = isPos ? '#00FF94' : '#FF3333';

  const getMarketMood = () => {
    if (!fearGreed) return { emoji: '😐', text: 'Loading...', color: 'var(--pp-text-3)', opportunity: '' };
    const v = fearGreed.value;
    if (v <= 20) return { emoji: '🟢', text: 'Great Buying Opportunity!', color: '#00FF94', opportunity: 'Smart money is accumulating' };
    if (v <= 40) return { emoji: '🔵', text: 'Good Entry Zone', color: '#2979FF', opportunity: 'Favorable conditions for buying' };
    if (v <= 60) return { emoji: '😐', text: 'Neutral Market', color: 'var(--pp-text-2)', opportunity: 'Wait for better setups' };
    if (v <= 80) return { emoji: '🟡', text: 'Getting Heated', color: '#FFB000', opportunity: 'Consider taking profits' };
    return { emoji: '🔴', text: 'Extreme Greed', color: '#FF3333', opportunity: 'Caution advised' };
  };
  const mood = getMarketMood();
  const selectedMultiplierData = COPY_MULTIPLIERS.find(m => m.value === selectedMultiplier);

  const projectionMonthlyRate = useMemo(() => {
    if (projectionStrategyId === 'bundle' && strategies.length > 0) {
      const rates = strategies.map(s => Math.min(3.0, (Math.pow(1 + Math.min(s.total_return_pct, 200) / 100, 1/36) - 1) * 100));
      return rates.reduce((a, b) => a + b, 0) / rates.length;
    }
    const strat = strategies.find(s => s.strategy_id === projectionStrategyId) || topStrategy;
    return strat ? Math.min(3.0, (Math.pow(1 + Math.min(strat.total_return_pct, 200) / 100, 1/36) - 1) * 100) : 2.5;
  }, [projectionStrategyId, strategies, topStrategy]);

  const getProjectionValues = () => {
    const monthlyRate = projectionMonthlyRate * selectedMultiplier;
    const months = selectedPeriod / 30;
    const projected = Math.round(startingBalance * Math.pow(1 + monthlyRate / 100, months));
    const gain = projected - startingBalance;
    const gainPct = ((gain / startingBalance) * 100).toFixed(1);
    return { projected, gain, gainPct };
  };

  const proj = getProjectionValues();

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="flex flex-col items-center gap-4">
          <div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  // Onboarding cover: show ONLY rocket section when no accounts and not dismissed
  if (!hasRealAccounts && !onboardingDismissed) {
    return (
      <div className="page-container">
        <div style={{ padding: '48px 24px', textAlign: 'center', minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 72, marginBottom: 20, lineHeight: 1 }}>🚀</div>
          <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.5px' }}>Start Your Copy Trading Journey</h2>
          <p style={{ color: 'var(--pp-text-2)', fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>Copy institutional-grade algorithms and grow your portfolio automatically.<br />No trading experience required.</p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(41,121,255,0.1)', border: '1px solid rgba(41,121,255,0.25)', padding: '10px 20px', marginBottom: 28 }}>
            <span style={{ fontSize: 20 }}>⏰</span>
            <div style={{ textAlign: 'left' }}>
              <p className="mono" style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#2979FF' }}>24/7</p>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>AUTO COPY</p>
            </div>
          </div>
          <button onClick={() => navigate('/strategies')} className="btn-primary" style={{ width: '100%', maxWidth: 360, height: 56, fontSize: 15, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>Browse Strategies <span style={{ fontSize: 18 }}>→</span></button>
          <button onClick={() => navigate('/link-broker')} style={{ width: '100%', maxWidth: 360, height: 52, background: 'transparent', border: '1px solid #00FF94', color: '#00FF94', fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><span style={{ fontSize: 18 }}>💼</span> Link Your Broker Account</button>
          <button data-testid="preview-dashboard-btn" onClick={() => { setOnboardingDismissed(true); sessionStorage.setItem('pp_onboarding_dismissed', '1'); }} style={{ marginTop: 24, color: '#00ff88', textShadow: '0 0 8px #00ff88, 0 0 16px rgba(0,255,136,0.4)', fontWeight: 600, fontSize: 14, background: 'none', border: '1px solid rgba(0,255,136,0.3)', padding: '10px 20px', borderRadius: 8, cursor: 'pointer' }}>What happens when I link my broker? — Preview Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Back to Welcome + Link Broker — shown in preview mode for users with no real accounts */}
      {onboardingDismissed && !hasRealAccounts && (
        <div data-testid="preview-back-bar" style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--pp-border)' }}>
          <button
            data-testid="back-to-welcome-btn"
            onClick={() => { sessionStorage.removeItem('pp_onboarding_dismissed'); setOnboardingDismissed(false); }}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            ← Back to Welcome
          </button>
          <button
            data-testid="link-broker-btn"
            onClick={() => navigate('/link-broker')}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.3)', color: '#00FF94', padding: '10px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            + Link Broker Account
          </button>
        </div>
      )}

      {/* Milestone Modal */}
      {milestoneModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border-active)', padding: 32, maxWidth: 360, width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>{milestoneModal.title}</h3>
            <p style={{ color: 'var(--pp-text-2)', marginBottom: 24 }}>{milestoneModal.message}</p>
            <button onClick={async () => { await api.dismissMilestone(milestoneModal.milestone_id).catch(() => {}); setMilestoneModal(null); }} className="btn-primary" style={{ width: '100%', height: 48 }}>Continue</button>
          </div>
        </div>
      )}

      {/* Header — no toggle, single unified view */}

      {/* Header — no toggle, single unified view */}
      <div style={{ padding: '20px 20px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          {/* Bigger, bolder brand name */}
          <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
            <span style={{ fontSize: 28, fontWeight: 900, color: 'var(--pp-accent)', letterSpacing: '-1px', lineHeight: 1 }}>PROFIT</span>
            <span style={{ fontSize: 28, fontWeight: 900, color: 'var(--pp-primary)', letterSpacing: '-1px', lineHeight: 1 }}>PULSE</span>
          </div>
          {/* Greeting — larger, white, not grayed */}
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--pp-text)' }}>
            Welcome, {user?.name?.split(' ')[0] || 'Trader'}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--pp-text-2)', fontStyle: 'italic' }}>
            Have you checked your pulse today?
          </p>
        </div>
      </div>

      {/* PWA Install Prompt — Android/Chrome */}
      {canInstall && (
        <div style={{ margin: '8px 16px 0', background: 'rgba(41,121,255,0.08)', border: '1px solid rgba(41,121,255,0.3)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/icons/icon-192.png" alt="ProfitPulse" style={{ width: 36, height: 36, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>Add to Home Screen</p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>Install Profit Pulse as an app — faster access, no browser bar</p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={triggerInstall} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#2979FF', border: 'none', color: '#000', padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <Download size={13} /> Add
            </button>
            <button onClick={dismissPWA} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', fontSize: 18, padding: '4px 6px' }}>×</button>
          </div>
        </div>
      )}

      {/* PWA Install Banner — iOS Safari */}
      {showIOSBanner && (
        <div style={{ margin: '8px 16px 0', background: 'rgba(41,121,255,0.08)', border: '1px solid rgba(41,121,255,0.3)', padding: '14px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flex: 1 }}>
              <img src="/icons/icon-192.png" alt="ProfitPulse" style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 8 }} />
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>Add to Home Screen</p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.6 }}>
                  Tap <strong style={{ color: '#2979FF' }}>Share</strong> <span style={{ fontSize: 14 }}>⬆</span> at the bottom of Safari, then tap{' '}
                  <strong style={{ color: '#2979FF' }}>"Add to Home Screen"</strong>
                </p>
              </div>
            </div>
            <button onClick={dismissPWA} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', fontSize: 20, padding: '0 0 0 8px', flexShrink: 0 }}>×</button>
          </div>
        </div>
      )}

      {/* Preview / Live toggle — always available */}
      {isPreviewMode === null ? null : isPreviewMode ? (
        <button
          data-testid="preview-mode-banner"
          onClick={() => { if (hasRealAccounts) api.setPreviewMode(false).then(() => loadData()).catch(() => {}); else navigate('/link-broker'); }}
          style={{ width: '100%', background: 'rgba(255,176,0,0.08)', border: 'none', borderTop: '1px solid rgba(255,176,0,0.3)', borderBottom: '1px solid rgba(255,176,0,0.3)', padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        >
          <Eye size={14} color="#FFB000" />
          <span style={{ fontSize: 12, color: '#FFB000', flex: 1, textAlign: 'left' }}>
            {hasRealAccounts ? '👁 Preview Mode — tap to show live data' : '👁 Preview — connect your broker to see live data'}
          </span>
          <span style={{ fontSize: 11, color: '#FFB000', fontWeight: 700 }}>›</span>
        </button>
      ) : (
        <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="live-dot" />
            <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.15em', color: 'var(--pp-success)' }}>LIVE</span>
          </div>
          <button onClick={() => api.setPreviewMode(true).then(() => loadData()).catch(() => {})}
            style={{ background: 'none', border: 'none', color: '#FFB000', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Eye size={11} color="#FFB000" /> View Preview
          </button>
        </div>
      )}

      {/* Account Type + Platform filter chips — shown when real accounts exist */}
      {accounts.length > 0 && (
        <div style={{ padding: '0 16px', marginBottom: 4 }}>
          {/* Row 1: All / Retail / Prop Firm */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, overflowX: 'auto', paddingBottom: 2 }}>
            {[
              { key: 'all',       label: '🌐 All',       },
              { key: 'retail',    label: '🏦 Retail',    },
              { key: 'prop_firm', label: '🏆 Prop Firm', },
            ].map(({ key, label }) => {
              const isActive = accountTypeFilter === key;
              return (
                <button key={key} onClick={() => setAccountTypeFilter(key)}
                  style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 20,
                    background: isActive ? (key === 'all' ? '#2979FF' : key === 'prop_firm' ? '#FFB000' : '#27272A') : 'transparent',
                    border: `1px solid ${isActive ? (key === 'all' ? '#2979FF' : key === 'prop_firm' ? '#FFB000' : '#3F3F46') : '#27272A'}`,
                    color: isActive ? '#fff' : 'var(--pp-text-3)',
                    fontSize: 12, fontWeight: isActive ? 700 : 400, cursor: 'pointer' }}>
                  {label}
                </button>
              );
            })}
          </div>
          {/* Row 2: All Platforms + per-platform chips */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
            <button onClick={() => setSelectedPlatform(null)}
              style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 20,
                background: !selectedPlatform ? 'var(--pp-primary)' : 'transparent',
                border: `1px solid ${!selectedPlatform ? 'var(--pp-primary)' : '#27272A'}`,
                color: !selectedPlatform ? '#fff' : 'var(--pp-text-3)',
                fontSize: 12, fontWeight: !selectedPlatform ? 700 : 400, cursor: 'pointer' }}>
              All Platforms
            </button>
            {Object.entries(platformGroups).map(([key, grp]) => {
              const isActive = selectedPlatform === key;
              const groupTotal = grp.accounts
                .filter(a => !deselectedIds.has(a.account_id))
                .reduce((s, a) => s + (a.balance || 0), 0);
              return (
                <button key={key} onClick={() => setSelectedPlatform(isActive ? null : key)}
                  style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 20,
                    background: isActive ? '#1a2438' : 'transparent',
                    border: `1px solid ${isActive ? '#2d3f5e' : '#27272A'}`,
                    color: isActive ? '#fff' : 'var(--pp-text-3)',
                    fontSize: 12, fontWeight: isActive ? 700 : 400, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{grp.icon}</span>
                  <span>{grp.label}</span>
                  {groupTotal > 0 && (
                    <span className="mono" style={{ fontSize: 10, color: isActive ? 'var(--pp-success)' : 'var(--pp-text-3)' }}>
                      ${groupTotal.toLocaleString()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {/* Per-account toggles when a platform is selected */}
          {selectedPlatform && (platformGroups[selectedPlatform]?.accounts || []).length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, overflowX: 'auto', paddingBottom: 2 }}>
              {(platformGroups[selectedPlatform]?.accounts || []).map(acct => {
                const excluded = deselectedIds.has(acct.account_id);
                return (
                  <button key={acct.account_id} onClick={() => toggleDeselect(acct.account_id)}
                    style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 20,
                      background: excluded ? 'transparent' : 'rgba(41,121,255,0.15)',
                      border: `1px solid ${excluded ? '#27272A' : '#2979FF'}`,
                      color: excluded ? '#52525B' : '#fff',
                      fontSize: 10, cursor: 'pointer', textDecoration: excluded ? 'line-through' : 'none' }}>
                    {acct.account_label || acct.broker_name || acct.account_id.slice(-6)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}



      {/* Portfolio Hero Card */}
      <div style={{ margin: '8px 16px', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: '20px 20px 0' }}>
        {hasInsurance && (
          <button onClick={() => setShowInsuranceInfo(!showInsuranceInfo)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.2)', padding: '5px 10px', marginBottom: 12, cursor: 'pointer' }}>
            <Shield size={14} color="var(--pp-success)" />
            <span style={{ fontSize: 11, color: 'var(--pp-success)' }}>Protected up to ${totalInsured}</span>
          </button>
        )}

        <p style={{ fontSize: 11, color: 'var(--pp-text-3)', margin: '0 0 2px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Total Portfolio</p>

        {/* Balance + Simple/Detail toggle on the same row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <p data-testid="dashboard-portfolio-value" className="mono"
            style={{ fontSize: 42, fontWeight: 700, margin: 0, color: '#FFFFFF', letterSpacing: '-1px', lineHeight: 1 }}>
            ${Math.round(effectiveEndVal).toLocaleString()}
          </p>
          {/* Toggle sits inline with the balance */}
          <div style={{ display: 'flex', gap: 5, paddingTop: 6, flexShrink: 0 }}>
            <button data-testid="dashboard-chart-toggle-simple" onClick={() => setChartDetailMode('simple')}
              style={{ padding: '5px 11px', background: chartDetailMode === 'simple' ? '#2979FF' : 'transparent', border: `1px solid ${chartDetailMode === 'simple' ? '#2979FF' : 'var(--pp-border)'}`, color: chartDetailMode === 'simple' ? '#000' : 'var(--pp-text-3)', fontSize: 10, fontWeight: 700, cursor: 'pointer', borderRadius: 12 }}>
              Simple
            </button>
            <button data-testid="dashboard-chart-toggle-detail" onClick={() => setChartDetailMode('detail')}
              style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '5px 11px', background: chartDetailMode === 'detail' ? '#2979FF' : 'transparent', border: `1px solid ${chartDetailMode === 'detail' ? '#2979FF' : 'var(--pp-border)'}`, color: chartDetailMode === 'detail' ? '#000' : 'var(--pp-text-3)', fontSize: 10, fontWeight: 700, cursor: 'pointer', borderRadius: 12 }}>
              <span style={{ fontSize: 11 }}>〰</span> Detail
            </button>
          </div>
        </div>

        {/* Started line — no PROJECTED badge here */}
        <p className="mono" style={{ fontSize: 11, color: '#71717A', margin: '4px 0 0' }}>
          Total Deposits: <span style={{ color: '#2979FF', fontWeight: 700, fontSize: 13 }}>${Math.round(effectiveStartingBalance).toLocaleString()}</span>
        </p>

        {/* Returns row — PROJECTED PREVIEW badge lives here */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontSize: 16, fontWeight: 600, color: isPos ? '#00FF94' : '#FF3333' }}>
            {isPos ? '+' : ''}${periodPnl.toLocaleString()}
          </span>
          <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: isPos ? '#00FF94' : '#FF3333' }}>
            ({isPos ? '+' : ''}{periodReturnDisplay}%)
          </span>
          {/* PROJECTED badge moved here — next to the green returns */}
          {isProjected && (
            <span style={{ fontSize: 9, background: 'rgba(41,121,255,0.15)', border: '1px solid rgba(41,121,255,0.3)', color: '#2979FF', padding: '2px 7px', fontWeight: 700, letterSpacing: '0.1em' }}>
              PROJECTED PREVIEW
            </span>
          )}
        </div>

        {/* Insurance note when portfolio is in red */}
        {periodReturn < 0 && hasInsurance && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,255,148,0.08)', padding: '8px 10px', marginTop: 8, border: '1px solid rgba(0,255,148,0.2)' }}>
            <Shield size={14} color="#00FF94" />
            <span style={{ color: '#00FF94', fontSize: 12 }}>Your insurance covers losses up to ${totalInsured}. You'll break even!</span>
          </div>
        )}

        {/* Chart — 30% taller, uses detail (noisy) or smooth data */}
        <div data-testid="dashboard-equity-chart" style={{ position: 'relative', height: 210, marginTop: 10, marginLeft: -20, marginRight: -20, overflow: 'hidden', background: '#050505' }}>
          {showPerAccountChart ? (
            <MultiSeriesEquityChart series={perAccountSeries} height={210} />
          ) : (
            <EquityChart data={displayChartData} color={chartColor} height={210} />
          )}
          {hasRealEquity && (
            <div data-testid="dashboard-equity-source-badge" style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,255,148,0.15)', border: '1px solid rgba(0,255,148,0.35)', color: '#00FF94', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '2px 6px' }}>
              LIVE
            </div>
          )}
          {equityLoading && (
            <div style={{ position: 'absolute', top: 8, right: 8, fontSize: 9, color: '#71717A', letterSpacing: '0.08em' }}>SYNCING…</div>
          )}
          {hasInsurance && (
            <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,255,148,0.2)', padding: '3px 6px', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Shield size={10} color="#00FF94" />
            </div>
          )}
        </div>

        {/* Per-account legend — only shown when per_account view is active */}
        {showPerAccountChart && (
          <div data-testid="dashboard-equity-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '10px 0 0' }}>
            {perAccountSeries.map((s) => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                <span style={{ width: 10, height: 2, background: s.color, display: 'inline-block' }} />
                <span style={{ color: '#E4E4E7', fontWeight: 600 }}>{s.label}</span>
                <span className="mono" style={{ color: s.delta >= 0 ? '#00FF94' : '#FF3333' }}>
                  {s.delta >= 0 ? '+' : ''}${Math.round(s.delta).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* All / Per-Account view toggle — only shown when real data is present */}
        {hasRealEquity && perAccountSeries.length > 1 && (
          <div style={{ display: 'flex', gap: 6, paddingTop: 10, justifyContent: 'center' }}>
            <button data-testid="dashboard-chart-view-all" onClick={() => setChartViewMode('all')}
              style={{ padding: '4px 12px', background: chartViewMode === 'all' ? '#2979FF' : 'transparent', border: `1px solid ${chartViewMode === 'all' ? '#2979FF' : 'var(--pp-border)'}`, color: chartViewMode === 'all' ? '#000' : 'var(--pp-text-3)', fontSize: 10, fontWeight: 700, cursor: 'pointer', borderRadius: 12 }}>
              All Combined
            </button>
            <button data-testid="dashboard-chart-view-per-account" onClick={() => setChartViewMode('per_account')}
              style={{ padding: '4px 12px', background: chartViewMode === 'per_account' ? '#2979FF' : 'transparent', border: `1px solid ${chartViewMode === 'per_account' ? '#2979FF' : 'var(--pp-border)'}`, color: chartViewMode === 'per_account' ? '#000' : 'var(--pp-text-3)', fontSize: 10, fontWeight: 700, cursor: 'pointer', borderRadius: 12 }}>
              Per Account
            </button>
          </div>
        )}

        {/* Period Selector — Robinhood-style underline */}
        <div data-testid="dashboard-period-selector" style={{ display: 'flex', justifyContent: 'space-around', paddingTop: 12, borderTop: '1px solid var(--pp-border)' }}>
          {CHART_PERIODS.map(p => (
            <button key={p.id} data-testid={`period-${p.id}`} onClick={() => setChartPeriod(p.id)}
              style={{ background: 'none', border: 'none', color: chartPeriod === p.id ? '#fff' : 'var(--pp-text-3)', fontSize: 11, fontWeight: chartPeriod === p.id ? 700 : 400, cursor: 'pointer', padding: '4px 2px', borderBottom: chartPeriod === p.id ? '2px solid #2979FF' : '2px solid transparent', transition: 'color 150ms, border-color 150ms' }}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Share Button — opens preview modal with branded performance card */}
        <button
          data-testid="dashboard-share-progress"
          onClick={async () => {
            setShareModal(true);
            setShareCardLoading(true);
            try {
              const periodCfg = CHART_PERIODS.find(p => p.id === chartPeriod) || CHART_PERIODS[5];
              const blob = await renderShareCard({
                traderName:      user?.name || user?.full_name || user?.first_name || (user?.email || 'Trader').split('@')[0],
                period:          chartPeriod,
                periodLabel:     periodCfg.labelText,
                currentEquity:   effectiveEndVal,
                startingBalance: effectiveStartingBalance,
                periodReturn,
                periodPnl,
                chartData:       displayChartData,
                isPositive:      isPos,
              });
              setShareCardBlob(blob);
              setShareCardUrl(URL.createObjectURL(blob));
            } catch (e) { console.error('share card render failed', e); }
            setShareCardLoading(false);
          }}
          style={{ borderTop: '1px solid var(--pp-border)', borderLeft: 0, borderRight: 0, borderBottom: 0, background: 'none', padding: '12px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', width: '100%' }}>
          <Share2 size={14} color="#2979FF" />
          <span style={{ fontSize: 13, color: '#2979FF', fontWeight: 600 }}>Share Your Progress</span>
        </button>
      </div>


      {/* Share Performance Card Modal */}
      {shareModal && (
        <div data-testid="share-card-modal-backdrop"
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
             onClick={() => {
               if (shareCardUrl) URL.revokeObjectURL(shareCardUrl);
               setShareModal(false); setShareCardUrl(null); setShareCardBlob(null);
             }}>
          <div data-testid="share-card-modal"
               style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, width: 420, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 13, letterSpacing: '0.1em' }}>SHARE YOUR PERFORMANCE</p>
              <button data-testid="share-card-close"
                onClick={() => {
                  if (shareCardUrl) URL.revokeObjectURL(shareCardUrl);
                  setShareModal(false); setShareCardUrl(null); setShareCardBlob(null);
                }}
                style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>

            {/* Preview */}
            <div data-testid="share-card-preview"
                 style={{ aspectRatio: '1080/1350', background: '#050505', border: '1px solid var(--pp-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 14 }}>
              {shareCardLoading ? (
                <span style={{ fontSize: 12, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>RENDERING…</span>
              ) : shareCardUrl ? (
                <img src={shareCardUrl} alt="performance card preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>Failed to render</span>
              )}
            </div>

            <p style={{ margin: '0 0 14px', fontSize: 11, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
              Tip: Native share lets you post straight to Instagram, X, or any installed app.
              Download saves a 1080×1350 PNG ready for stories &amp; posts.
            </p>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button data-testid="share-card-share"
                disabled={!shareCardBlob}
                onClick={async () => {
                  if (!shareCardBlob) return;
                  const fileName = `profitpulse-${chartPeriod.toLowerCase()}-${Date.now()}.png`;
                  await shareBlob(shareCardBlob, fileName,
                    'My ProfitPulse performance',
                    `${periodReturn >= 0 ? '+' : ''}${periodReturnDisplay}% on ProfitPulse · profit-pulse.pro`);
                }}
                style={{ flex: 1, height: 44, background: '#2979FF', border: 'none', color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: '0.08em', cursor: shareCardBlob ? 'pointer' : 'not-allowed', opacity: shareCardBlob ? 1 : 0.5 }}>
                <Share2 size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />SHARE
              </button>
              <button data-testid="share-card-download"
                disabled={!shareCardBlob}
                onClick={() => {
                  if (!shareCardBlob) return;
                  const fileName = `profitpulse-${chartPeriod.toLowerCase()}-${Date.now()}.png`;
                  downloadBlob(shareCardBlob, fileName);
                }}
                style={{ flex: 1, height: 44, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', fontWeight: 700, fontSize: 12, letterSpacing: '0.08em', cursor: shareCardBlob ? 'pointer' : 'not-allowed', opacity: shareCardBlob ? 1 : 0.5 }}>
                <Download size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />DOWNLOAD
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Insurance Info */}
      {showInsuranceInfo && (
        <div style={{ margin: '0 16px', background: 'var(--pp-surface)', border: '1px solid rgba(0,255,148,0.3)', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <Shield size={20} color="var(--pp-success)" />
            <span style={{ fontWeight: 700 }}>Copy-Trade Insurance</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--pp-text-2)', lineHeight: 1.6, margin: 0 }}>Your portfolio is protected! If you end a 30-day cycle negative, you'll be reimbursed up to ${totalInsured}.</p>
          <button onClick={() => navigate('/copy-dashboard')} style={{ background: 'none', border: 'none', color: 'var(--pp-accent)', fontSize: 13, cursor: 'pointer', marginTop: 8, padding: 0 }}>Manage Insurance →</button>
        </div>
      )}

      {/* Account Portfolio + Copy Dashboard — ABOVE active copies */}
      <div style={{ margin: '12px 16px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button onClick={() => navigate('/account-portfolio')} style={{ background: 'var(--pp-surface)', border: '1px solid rgba(155,35,53,0.4)', padding: 14, textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart2 size={18} color="#9B2335" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#9B2335' }}>Account Portfolio</span>
          </div>
          <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>Live P&L · all accounts · open floats</p>
        </button>
        <button onClick={() => navigate('/copy-dashboard')} style={{ background: 'var(--pp-surface)', border: '1px solid rgba(41,121,255,0.4)', padding: 14, textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Copy size={18} color="#2979FF" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#2979FF' }}>Copy Dashboard</span>
          </div>
          <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>Risk per account · positions · strategies</p>
        </button>
      </div>

      {/* Active Copies */}
      {activeCopies.length > 0 && (
        <div style={{ margin: '16px 16px 0' }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, letterSpacing: '0.05em' }}>📊 Your Active Copies</p>
          {activeCopies.map(copy => (
            <button key={copy.subscription_id} onClick={() => navigate(`/copy-settings/${copy.subscription_id}`)} style={{ width: '100%', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 8, textAlign: 'left', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{copy.strategy_name}</p>
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>{copy.instrument}</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {copy.insurance_active && <Shield size={14} color="#00FF94" />}
                  <Settings size={16} color="var(--pp-text-3)" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {[
                  { label: 'Win', value: `${copy.win_rate}%` },
                  { label: 'Today', value: `${copy.today_pnl >= 0 ? '+' : ''}$${copy.today_pnl}`, color: copy.today_pnl >= 0 ? '#00FF94' : '#FF3333' },
                  { label: 'Total', value: `${copy.total_pnl >= 0 ? '+' : ''}$${copy.total_pnl}`, color: copy.total_pnl >= 0 ? '#00FF94' : '#FF3333' },
                ].map(m => (
                  <div key={m.label} style={{ textAlign: 'center' }}>
                    <p className="mono" style={{ margin: 0, fontSize: 14, fontWeight: 600, color: m.color || 'var(--pp-text)' }}>{m.value}</p>
                    <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>{m.label.toUpperCase()}</p>
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Market Mood + VIX side by side */}
      <div style={{ margin: '16px 16px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {/* Fear & Greed */}
        <div data-testid="dashboard-market-mood-card" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 18 }}>{mood.emoji}</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: mood.color, lineHeight: 1.2 }}>{mood.text}</p>
              {mood.opportunity && <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', lineHeight: 1.3 }}>{mood.opportunity}</p>}
            </div>
          </div>
          <p className="section-label" style={{ marginBottom: 3 }}>Fear & Greed</p>
          <p className="mono" style={{ fontSize: 26, fontWeight: 800, margin: '2px 0', color: mood.color }}>{fearGreed?.value ?? '—'}</p>
          {fearGreed?.value != null && (
            <div style={{ marginTop: 6 }}>
              <div style={{ height: 3, background: 'linear-gradient(to right, #FF3333, #FFB000, #00FF94)', position: 'relative', marginBottom: 3 }}>
                <div style={{ position: 'absolute', top: -2, left: `${fearGreed.value}%`, width: 2, height: 7, background: '#fff', transform: 'translateX(-50%)' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 8, color: '#FF3333', fontWeight: 700 }}>FEAR</span>
                <span style={{ fontSize: 8, color: '#A1A1AA' }}>NEUTRAL</span>
                <span style={{ fontSize: 8, color: '#00FF94', fontWeight: 700 }}>GREED</span>
              </div>
            </div>
          )}
        </div>
        {/* VIX */}
        <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, textAlign: 'center' }}>
          <p className="section-label" style={{ marginBottom: 6 }}>VIX Volatility</p>
          <p className="mono" style={{ fontSize: 30, fontWeight: 800, margin: '4px 0',
            color: (vix?.value || 0) > 25 ? '#FF3333' : (vix?.value || 0) > 15 ? '#FFB000' : '#00FF94' }}>
            {vix?.value?.toFixed(1) ?? '—'}
          </p>
          <p className="mono" style={{ margin: 0, fontSize: 10, fontWeight: 700,
            color: (vix?.change || 0) >= 0 ? '#FF3333' : '#00FF94' }}>
            {(vix?.change || 0) >= 0 ? '+' : ''}{vix?.change?.toFixed(2) || '0'}%
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
            color: (vix?.value || 0) > 30 ? '#FF3333' : (vix?.value || 0) > 20 ? '#FFB000' : '#00FF94' }}>
            {(vix?.value || 0) > 30 ? 'HIGH VOLATILITY' : (vix?.value || 0) > 20 ? 'ELEVATED RISK' : '✅ LOW VOLATILITY'}
          </p>
        </div>
      </div>


      {/* Edge Score — always shows with sub-components */}
      <div data-testid="dashboard-edge-score-card" style={{ margin: '12px 16px 0', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 13, letterSpacing: '0.12em', color: 'var(--pp-text-3)' }}>🎯 COMPOSITE EDGE SCORE</p>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>Higher = Better opportunity</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
            <span className="mono" style={{ fontSize: 28, fontWeight: 700, color: 'var(--pp-text)' }}>{edgeScore?.score || 0}</span>
            <span style={{ fontSize: 14, color: 'var(--pp-text-3)' }}>/100</span>
          </div>
        </div>
        <div className="progress-bar">
          <div className="progress-bar-fill" style={{ width: `${edgeScore?.score || 0}%`, background: (edgeScore?.score || 0) >= 60 ? '#00FF94' : '#2979FF' }} />
        </div>
        {(edgeScore?.score || 0) >= 60 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, color: '#00FF94', fontSize: 12 }}>
            <TrendingUp size={14} />
            <span>Conditions are favorable! Great time to copy.</span>
          </div>
        )}
        {/* Sub-component breakdown — always visible */}
        {edgeScore?.components && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--pp-border)' }}>
            {[
              { label: 'STRATEGY',   value: edgeScore.components.strategy_performance?.toFixed(1) || '—' },
              { label: 'DIVERGENCE', value: edgeScore.components.sentiment_divergence?.toFixed(1) || '—' },
              { label: 'CONDITIONS', value: edgeScore.components.market_conditions?.toFixed(1) || '—' },
            ].map(c => (
              <div key={c.label} style={{ textAlign: 'center' }}>
                <p className="mono" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#2979FF' }}>{c.value}</p>
                <p style={{ margin: 0, fontSize: 8, color: 'var(--pp-text-3)', letterSpacing: '0.12em', marginTop: 3 }}>{c.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Growth Projector — always visible */}
      <div data-testid="dashboard-growth-projector-card" style={{ margin: '12px 16px 0', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16 }}>
        <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 15 }}>📈 Growth Projector</p>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--pp-text-3)' }}>See your potential with Profit Pulse</p>


        {/* Strategy Selector */}
        <p className="section-label" style={{ marginBottom: 8 }}>Select Strategy</p>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 8 }}>
          <button onClick={() => setProjectionStrategyId('bundle')} style={{ flexShrink: 0, padding: '5px 12px', background: projectionStrategyId === 'bundle' ? 'var(--pp-accent)' : 'transparent', border: `1px solid ${projectionStrategyId === 'bundle' ? 'var(--pp-accent)' : 'var(--pp-border)'}`, color: projectionStrategyId === 'bundle' ? '#000' : 'var(--pp-text-2)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Bundle Avg</button>
          {strategies.map(s => (
            <button key={s.strategy_id} onClick={() => setProjectionStrategyId(s.strategy_id)} style={{ flexShrink: 0, padding: '5px 12px', background: projectionStrategyId === s.strategy_id ? 'var(--pp-accent)' : 'transparent', border: `1px solid ${projectionStrategyId === s.strategy_id ? 'var(--pp-accent)' : 'var(--pp-border)'}`, color: projectionStrategyId === s.strategy_id ? '#000' : 'var(--pp-text-2)', fontSize: 11, cursor: 'pointer' }}>{s.name}</button>
          ))}
        </div>

        {/* Source attribution */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, padding: '6px 10px', background: 'var(--pp-overlay)' }}>
          <span style={{ fontSize: 12 }}>⚡</span>
          <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>
            {projectionStrategyId === 'bundle'
              ? <><span style={{ color: 'var(--pp-text)' }}>Bundle avg of</span> <strong style={{ color: 'var(--pp-text)' }}>{strategies.length} strategies</strong></>
              : <><span style={{ color: 'var(--pp-text-3)' }}>Based on </span><strong style={{ color: 'var(--pp-text)' }}>{strategies.find(s => s.strategy_id === projectionStrategyId)?.name || topStrategy?.name}</strong></>
            }
            {' — '}<span style={{ color: '#00FF94', fontWeight: 700 }}>{projectionMonthlyRate.toFixed(1)}% avg/month</span>
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--pp-text-2)' }}>Your Starting Capital</span>
          <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>${startingBalance.toLocaleString()}{startingBalance === 400000 && <span style={{ color: 'var(--pp-text-3)', fontSize: 11 }}> (example)</span>}</span>
        </div>

        {/* Period Selector */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {PROJECTION_PERIODS.map(p => (
            <button key={p.days} onClick={() => setSelectedPeriod(p.days)} style={{ flex: 1, padding: '6px 0', background: selectedPeriod === p.days ? 'var(--pp-primary)' : 'transparent', border: `1px solid ${selectedPeriod === p.days ? 'var(--pp-primary)' : 'var(--pp-border)'}`, color: selectedPeriod === p.days ? '#fff' : 'var(--pp-text-2)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{p.label}</button>
          ))}
        </div>

        {/* Multiplier — Copy Size */}
        <p className="section-label" style={{ marginBottom: 8 }}>Copy Size (Risk Level)</p>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 10 }}>
          {COPY_MULTIPLIERS.map(m => (
            <button key={m.value} onClick={() => setSelectedMultiplier(m.value)} style={{ flexShrink: 0, padding: '6px 10px', background: selectedMultiplier === m.value ? (m.value > 1 ? '#FFB000' : '#00FF94') : 'transparent', border: `1px solid ${selectedMultiplier === m.value ? (m.value > 1 ? '#FFB000' : '#00FF94') : 'var(--pp-border)'}`, color: selectedMultiplier === m.value ? '#000' : 'var(--pp-text-2)', fontSize: 11, cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontWeight: 700 }}>{m.label}</div>
              <div style={{ fontSize: 9, marginTop: 2 }}>{m.desc}</div>
            </button>
          ))}
        </div>

        {/* Active rate note */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', marginBottom: 12, background: selectedMultiplier > 1 ? 'rgba(255,176,0,0.08)' : 'rgba(0,255,148,0.08)', border: `1px solid ${selectedMultiplier > 1 ? 'rgba(255,176,0,0.25)' : 'rgba(0,255,148,0.25)'}` }}>
          <TrendingUp size={14} color={selectedMultiplier > 1 ? '#FFB000' : '#00FF94'} />
          <span style={{ fontSize: 12, color: selectedMultiplier > 1 ? '#FFB000' : '#00FF94' }}>
            Projecting at <strong>{(projectionMonthlyRate * selectedMultiplier).toFixed(1)}%/month</strong>
            {' '}({selectedMultiplierData?.riskNote})
          </span>
        </div>

        {/* Mini projection chart */}
        <div style={{ marginBottom: 12, background: 'var(--pp-overlay)', padding: '8px 8px 4px' }}>
          <ProjectionChart startBalance={startingBalance} monthlyRate={projectionMonthlyRate * selectedMultiplier} selectedPeriod={selectedPeriod} height={110} />
        </div>

        {/* Starting → Projected result */}
        <div style={{ background: 'var(--pp-overlay)', padding: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <p className="section-label" style={{ marginBottom: 4 }}>Starting Balance</p>
            <p className="mono" style={{ margin: 0, fontSize: 15, color: '#2979FF', fontWeight: 700 }}>${startingBalance.toLocaleString()}</p>
          </div>
          <span style={{ color: 'var(--pp-text-3)', fontSize: 18 }}>→</span>
          <div style={{ flex: 1.5, textAlign: 'center' }}>
            <p className="section-label" style={{ marginBottom: 4 }}>After {selectedPeriod} days</p>
            <p className="mono" style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#00FF94' }}>${proj.projected.toLocaleString()}</p>
            <p className="mono" style={{ margin: '3px 0 0', fontSize: 13, color: '#00FF94' }}>+${proj.gain.toLocaleString()} ({proj.gainPct}% gain)</p>
          </div>
        </div>

        <p style={{ fontSize: 10, color: 'var(--pp-text-3)', marginTop: 10, lineHeight: 1.6 }}>
          *Projection based on {topStrategy?.name || 'top strategy'} live performance{topStrategy?.backtest_period ? ` (since ${topStrategy.backtest_period.split(/[–-]/)[0]?.trim()})` : ''}. Past results do not guarantee future returns.
        </p>
      </div>

      {/* Recommendations — Diversify Your Portfolio */}
      {recommendations.length > 0 && (
        <div style={{ margin: '12px 16px 0' }}>
          <p style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700 }}>💡 Diversify Your Portfolio</p>
          {recommendations.map(rec => (
            <button key={rec.strategy_id} onClick={() => navigate(`/strategy/${rec.strategy_id}`)}
              style={{ width: '100%', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, marginBottom: 8, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{rec.name}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{rec.instrument} · {rec.win_rate}% win · +{rec.total_return_pct?.toFixed(0)}%</p>
                {rec.benefit_text && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#2979FF' }}>{rec.benefit_text}</p>}
              </div>
              <span style={{ color: 'var(--pp-text-3)', fontSize: 18, flexShrink: 0 }}>›</span>
            </button>
          ))}
        </div>
      )}

      {/* User Badges */}
      {dashboardData?.user_stats?.badges?.length > 0 && (
        <div style={{ margin: '12px 16px 0' }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700 }}>🏆 Your Badges</p>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
            {(dashboardData.user_stats.badge_details || []).map(badge => (
              <div key={badge.id} style={{ flexShrink: 0, background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: '10px 14px', textAlign: 'center', minWidth: 80 }}>
                <div style={{ fontSize: 22 }}>{badge.icon}</div>
                <div style={{ fontSize: 10, color: 'var(--pp-text-3)', marginTop: 4 }}>{badge.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hot Strategy */}
      {topStrategy && activeCopies.length === 0 && (
        <div style={{ margin: '12px 16px 0', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(155,35,53,0.15)', border: '1px solid rgba(155,35,53,0.3)', padding: '3px 10px', marginBottom: 10 }}>
            <span style={{ fontSize: 12 }}>🔥</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--pp-primary)', letterSpacing: '0.1em' }}>HOT STRATEGY</span>
          </div>
          <p style={{ margin: 0, fontWeight: 800, fontSize: 18 }}>{topStrategy.name}</p>
          <p style={{ margin: '2px 0 12px', fontSize: 12, color: 'var(--pp-text-3)' }}>{topStrategy.instrument}</p>
          <div style={{ display: 'flex', gap: 0, marginBottom: 14, border: '1px solid var(--pp-border)' }}>
            <div style={{ flex: 1, textAlign: 'center', padding: '10px 8px' }}>
              <p className="mono" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{topStrategy.win_rate}%</p>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)' }}>Win Rate</p>
            </div>
            <div style={{ width: 1, background: 'var(--pp-border)' }} />
            <div style={{ flex: 1, textAlign: 'center', padding: '10px 8px' }}>
              <p className="mono" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#00FF94' }}>+{topStrategy.total_return_pct}%</p>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)' }}>Total Return</p>
            </div>
          </div>
          <button onClick={() => navigate(`/strategy/${topStrategy.strategy_id}`)} className="btn-primary" style={{ width: '100%', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13 }}>
            Start Copying <span style={{ fontSize: 16 }}>→</span>
          </button>
        </div>
      )}

      {/* Quick Stats */}
      <div style={{ margin: '12px 16px 0', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {[
          { value: activeCopies.length, label: 'Active Copies' },
          { value: dashboardData?.user_stats?.total_copied_trades || 0, label: 'Trades Copied' },
          { value: `${dashboardData?.user_stats?.current_streak || 0}🔥`, label: 'Win Streak', color: '#FFB000' },
        ].map((s, i) => (
          <div key={i} style={{ padding: '14px 8px', textAlign: 'center', borderRight: i < 2 ? '1px solid var(--pp-border)' : 'none' }}>
            <p className="mono" style={{ margin: 0, fontSize: 22, fontWeight: 800, color: s.color || 'var(--pp-text)' }}>{s.value}</p>
            <p style={{ margin: '3px 0 0', fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.08em' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Encouragement footer */}
      <div style={{ margin: '12px 16px 0', background: 'rgba(41,121,255,0.05)', border: '1px solid rgba(41,121,255,0.15)', padding: '14px 16px', textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
          You're on the right track! Keep copying and let compound growth work for you. 💪
        </p>
      </div>


      <div style={{ height: 20 }} />
    </div>
  );
}
